// packages/web/src/components/learner/LearnerReport.tsx
// Reemplaza la función LearnerReport (y su FeedbackCard) dentro de ReportViewer.tsx.
// Mismos datos y mismas derivaciones que hoy; solo cambia la presentación.
import { useEffect, useState } from 'react';
import { ArrowRight, BookOpen, LogOut, Star, Target } from 'lucide-react';
import { dimensionLevel, masteryBands, shouldTriggerRemedial } from '@marzano/contracts';
import { api, type ScoreBreakdownResponse } from '../../api';
import { RemedialTaskView } from '../RemedialTaskView';
import { ALL_DIMS, type Dictamen, type Dim } from '../../types';
import {
  CRITERIA_KID, Collapsible, DIM_KID, DIM_LEVEL, GuideBubble, STATE_KID, ScoreBar, SectionTitle, Wordmark,
  critStatus, firstName,
} from './ui';

export interface LearnerReportProps {
  dictamen: Dictamen;
  studentName: string;
  sessionId: string;
  masteryThreshold: number;
  onBackToList: () => void;
}

export function LearnerReport(props: LearnerReportProps) {
  const { dictamen, masteryThreshold } = props;
  const dimScores: Record<Dim, number> = {
    recuperacion: dictamen.dim_recuperacion,
    comprension: dictamen.dim_comprension,
    analisis: dictamen.dim_analisis,
    transferencia: dictamen.dim_transferencia,
  };
  const levelOf = (s: number) => dimensionLevel(s, masteryThreshold);
  const g = Math.round(dictamen.global_score);
  const bands = masteryBands(masteryThreshold);
  const st = STATE_KID[dictamen.state];
  const next =
    dictamen.state === 'no_evidencia' ? { at: bands.noEvidence, label: STATE_KID.en_desarrollo.label }
    : dictamen.state === 'en_desarrollo' ? { at: bands.sufficient, label: STATE_KID.suficiente.label }
    : dictamen.state === 'suficiente' ? { at: bands.deep, label: STATE_KID.profundo.label }
    : null;
  const toNext = next ? Math.max(1, Math.ceil(next.at - dictamen.global_score)) : 0;

  // Misma lógica que la versión anterior; textos sin voseo ni emoji.
  const strongDims = ALL_DIMS.filter((d) => levelOf(dimScores[d] ?? 0) === 'strong');
  const strengths = strongDims.length > 0
    ? strongDims.map((d) => `Te fue muy bien en ${DIM_KID[d].kid}.`)
    : ['Terminaste la evaluación. ¡Eso ya es un logro!'];
  const toWork = [...(dictamen.gaps_detected?.gaps ?? []), ...(dictamen.gaps_detected?.common_errors_observed ?? [])];
  const nextSteps = dictamen.recommendations?.student ?? [];
  const name = firstName(props.studentName);

  return (
    <main className="min-h-[100dvh] bg-crema font-kid text-slate-900">
      <div className="mx-auto flex max-w-[640px] flex-col gap-4 px-4 pb-10 pt-5">
        <header className="flex items-center justify-between"><Wordmark /></header>

        <GuideBubble avatar={64}>
          <strong>¡Terminaste{name ? `, ${name}` : ''}!</strong> Mira cómo te fue.
        </GuideBubble>

        {/* 1 · Puntaje global con las 4 bandas del hito */}
        <section aria-labelledby="g-t" className="card-kid flex flex-col gap-3.5 p-5">
          <h2 id="g-t" className="text-base font-bold text-slate-700">Tu puntaje</h2>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-baseline gap-1.5">
              <span className="text-7xl font-bold leading-none text-marzano-600">{g}</span>
              <span className="text-xl text-slate-600">de 100</span>
            </p>
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-2 text-[17px] font-bold ${st.cls}`}>
              <span aria-hidden>{st.icon}</span>{st.label}
            </span>
          </div>
          <div className="relative">
            <ScoreBar value={dictamen.global_score} h="h-5" label={`Puntaje global ${g} de 100`} />
            {[bands.noEvidence, bands.sufficient, bands.deep].map((b) => (
              <span key={b} aria-hidden className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${b}%` }} />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600" aria-label="Niveles">
            {(['no_evidencia', 'en_desarrollo', 'suficiente', 'profundo'] as const).map((k) => (
              <li key={k} className={k === dictamen.state ? 'font-bold text-marzano-600' : ''}>
                {STATE_KID[k].icon} {STATE_KID[k].label}
              </li>
            ))}
          </ul>
          {next && <p className="text-base text-slate-700">Te faltan <strong>{toNext} puntos</strong> para llegar a {next.label}</p>}
        </section>

        {/* 2 · Tarjetas */}
        <Feedback icon={<Star aria-hidden className="h-5 w-5" />} title="Lo que hiciste muy bien" items={strengths} cls="bg-emerald-50" tcls="text-emerald-800" />
        <Feedback icon={<Target aria-hidden className="h-5 w-5" />} title="Para practicar"
          items={toWork.length ? toWork : ['Todo bien por ahora. Sigue atento en la próxima clase.']} cls="bg-amber-50" tcls="text-amber-800" />
        <Feedback icon={<ArrowRight aria-hidden className="h-5 w-5" />} title="Tu próximo paso"
          items={nextSteps.length ? nextSteps : ['Repasa tus apuntes de clase y practica con ejemplos nuevos.']} cls="bg-marzano-50" tcls="text-marzano-600" />

        {/* 3 · Cómo te fue en cada área */}
        <SectionTitle>¿Cómo te fue en cada área?</SectionTitle>
        <ul className="card-kid px-[18px] py-2">
          {ALL_DIMS.map((d) => {
            const K = DIM_KID[d], v = Math.round(dimScores[d] ?? 0), L = DIM_LEVEL[levelOf(dimScores[d] ?? 0)];
            return (
              <li key={d} className="flex flex-col gap-2 border-b border-crema-200 py-3.5 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white ${K.bar}`}>{K.n}</span>
                  <strong className="min-w-0 flex-1 text-lg">{K.kid}</strong>
                  <span className="text-[22px] font-bold">{v}</span>
                </div>
                <ScoreBar value={v} color={K.bar} label={`${K.kid}: ${v} de 100`} />
                <span className={`text-[15px] font-bold ${L.cls}`}>{L.icon} {L.label}</span>
              </li>
            );
          })}
        </ul>

        {/* 4 y 5 · Cálculo + preguntas (endpoint /breakdown) */}
        <LearnerBreakdown sessionId={props.sessionId} weights={dictamen.weights_marzano} globalScore={g} />

        {shouldTriggerRemedial(dictamen.state) && (
          <section className="card-kid flex flex-col gap-2 p-[18px]">
            <h2 className="flex items-center gap-2 text-lg font-bold text-marzano-600"><BookOpen aria-hidden className="h-5 w-5" />Guía para practicar</h2>
            <RemedialTaskView sessionId={props.sessionId} />
          </section>
        )}

        <button type="button" onClick={props.onBackToList} className="btn-kid mt-2 border-2 border-slate-300 bg-white text-marzano-600">
          <LogOut aria-hidden className="h-5 w-5" />Salir
        </button>
      </div>
    </main>
  );
}

function LearnerBreakdown({ sessionId, weights, globalScore }: { sessionId: string; weights: Dictamen['weights_marzano']; globalScore: number }) {
  const [data, setData] = useState<ScoreBreakdownResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    void api.getBreakdown(sessionId).then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);
  if (!data) return null;
  const { scores, questions } = data;
  const num = (x: number) => (x < 10 ? x.toFixed(1).replace('.', ',') : String(Math.round(x)));

  return (
    <>
      {scores && (
        <>
          <SectionTitle>¿Cómo se calculó tu puntaje?</SectionTitle>
          <Collapsible title="? Cómo leer esto">
            <ol className="card-kid flex flex-col gap-3.5 p-[18px] text-[17px] leading-snug">
              <Step n={1}>Pío se fija en 4 cosas: si fuiste <strong>preciso</strong>, si explicaste <strong>a fondo</strong>, si lo hiciste <strong>sin ayuda</strong> y si dijiste <strong>por qué</strong>.</Step>
              <Step n={2}>La barra muestra cuántos <strong>puntos</strong> ganaste de los que <strong>se podían ganar</strong> en esa área.</Step>
              <Step n={3}>Eso se pasa a una escala de <strong>100</strong>. Si respondiste poco en un área, baja un poco, porque Pío vio menos.</Step>
              <Step n={4}>Tu puntaje final junta las 4 áreas. Algunas <strong>valen más</strong> que otras en este tema.</Step>
            </ol>
          </Collapsible>
          <div className="card-kid px-[18px] py-2">
            <table className="w-full text-left">
              <caption className="sr-only">Dimensión, criterios observados, puntaje, máximo posible, normalizado y peso</caption>
              <thead className="sr-only">
                <tr><th>Área</th><th>Criterios observados</th><th>Puntaje y máximo posible</th><th>Normalizado</th><th>Peso</th></tr>
              </thead>
              <tbody>
                {scores.dimensions.map((d) => {
                  const K = DIM_KID[d.dim];
                  const ratio = d.max_possible > 0 ? (d.raw_score / d.max_possible) * 100 : 0;
                  return (
                    <tr key={d.dim} className="flex flex-col gap-2 border-b border-crema-200 py-3.5">
                      <th scope="row" className="flex items-baseline justify-between gap-2 text-[17px] font-bold">
                        <span>{K.n}. {K.kid}</span>
                        <span className="text-sm font-normal text-slate-600">vale {weights[d.dim]}%</span>
                      </th>
                      <td className="flex flex-wrap gap-1.5">
                        {d.criteria_observed.length
                          ? d.criteria_observed.map((c) => <span key={c} className={`rounded-full px-2.5 py-1 text-sm font-bold ${K.soft} ${K.text}`}>{CRITERIA_KID[c]}</span>)
                          : <span className="text-sm text-slate-600">Pío no alcanzó a ver esta área</span>}
                      </td>
                      <td className="flex items-center gap-2.5">
                        <ScoreBar value={ratio} color={K.bar} h="h-2.5" label={`${K.kid}: ${num(d.raw_score)} de ${num(d.max_possible)} puntos`} />
                        <span className="shrink-0 text-[15px] text-slate-700">{num(d.raw_score)} de {num(d.max_possible)}</span>
                      </td>
                      <td className="text-[17px] font-bold">→ {Math.round(d.normalized_score)} de 100</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-baseline justify-between pb-3 pt-4">
              <strong className="text-lg">Puntaje final</strong>
              <strong className="text-[26px] text-marzano-600">{Math.round(scores.global_normalized ?? globalScore)}</strong>
            </div>
          </div>
        </>
      )}

      {questions.length > 0 && (
        <>
          <SectionTitle>Cada pregunta y su puntaje</SectionTitle>
          {/* Sin rúbrica ni respuesta esperada: pregunta, respuesta, puntaje y criterios. */}
          <ol className="flex flex-col gap-4">
            {questions.map((q) => {
              const K = q.dim ? DIM_KID[q.dim] : null;
              return (
                <li key={q.n} className="card-kid flex flex-col gap-3 p-[18px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[15px] font-bold text-slate-600">Pregunta {q.n}</span>
                    {K && <span className={`rounded-full px-2.5 py-1 text-sm font-bold ${K.soft} ${K.text}`}>{K.n} · {K.kid}</span>}
                  </div>
                  <p className="text-lg font-bold leading-snug text-marzano-600">{q.question_text}</p>
                  {q.answer_text && (
                    <div className="flex flex-col gap-1 rounded-2xl bg-crema px-3.5 py-3">
                      <span className="text-sm font-bold text-slate-600">Tú dijiste</span>
                      <p className="text-[17px] leading-snug">{q.answer_text}</p>
                    </div>
                  )}
                  {q.score !== null ? (
                    <div className="flex items-center gap-2.5">
                      <ScoreBar value={q.score} h="h-2.5" label={`Pregunta ${q.n}: ${q.score} de 100`} />
                      <strong className="shrink-0 text-lg">{q.score}<span className="text-sm font-normal text-slate-600"> de 100</span></strong>
                    </div>
                  ) : <span className="text-[15px] text-slate-600">Esta pregunta no tiene puntaje.</span>}
                  {q.criteria.length > 0 && (
                    <ul className="flex flex-wrap gap-2">
                      {q.criteria.map((c, i) => {
                        const s = critStatus(c.score);
                        return (
                          <li key={`${c.dim}-${c.criteria}-${i}`} className={`rounded-full px-2.5 py-1.5 text-[15px] font-bold ${s.cls}`}>
                            <span aria-hidden>{s.icon} </span>{CRITERIA_KID[c.criteria]}<span className="sr-only">: {s.sr}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </>
  );
}

function Feedback({ icon, title, items, cls, tcls }: { icon: React.ReactNode; title: string; items: readonly string[]; cls: string; tcls: string }) {
  return (
    <section className={`flex flex-col gap-1.5 rounded-kid px-[18px] py-4 ${cls}`}>
      <h2 className={`flex items-center gap-2 text-lg font-bold ${tcls}`}>{icon}{title}</h2>
      <ul className="flex flex-col gap-1.5 text-[17px] leading-snug">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-marzano-600 text-[15px] font-bold text-white">{n}</span>
      <span>{children}</span>
    </li>
  );
}
